/**
 * react-i18next 初始化 — V23 Session-2 Epic 5
 *
 * 默认 zh-CN,en 留壳子(本期不提取英文)。
 *
 * 用法:
 * ```tsx
 * import { useTranslation } from "react-i18next";
 * const { t } = useTranslation();
 * <Button>{t("common.cancel")}</Button>
 * ```
 *
 * 命名空间组织(本期按域拆):
 * - common: 通用按钮 / toast / 错误消息 / 侧边栏 / 布局
 * - poster / coaching / customer-service / kb / billing / admin / settings: 各域专属
 * - platform: 平台管理(Platform*, 权限, 租户)
 * - user: 用户管理 + 登录
 * - wecom: 企微 H5
 *
 * 渐进迁:本期只提取高频(标题 / 按钮 / toast / 错误),~100-200 处。
 * 剩余 ~3500 处中文 hardcoded 留 P2 渐进迁(见 default decision F4)。
 */
import i18n from "i18next";
import { initReactI18next } from "react-i18next";

import { brandI18nVars, getBrand, subscribeBrand } from "@/shared/brand/store";

import enAdmin from "@/locales/en/admin.json";
import enAdvisor from "@/locales/en/advisor.json";
import enAdvisorConversations from "@/locales/en/advisor-conversations.json";
import enBilling from "@/locales/en/billing.json";
import enCoaching from "@/locales/en/coaching.json";
import enCommon from "@/locales/en/common.json";
import enCustomerService from "@/locales/en/customer-service.json";
import enKb from "@/locales/en/kb.json";
import enLlmConfig from "@/locales/en/llm-config.json";
import enPlatform from "@/locales/en/platform.json";
import enPlatformPartners from "@/locales/en/platform-partners.json";
import enPartnerPortal from "@/locales/en/partner-portal.json";
import enPlatformPrivate from "@/locales/en/platform-private.json";
import enPoster from "@/locales/en/poster.json";
import enSettings from "@/locales/en/settings.json";
import enUser from "@/locales/en/user.json";
import enWecom from "@/locales/en/wecom.json";
import zhAdmin from "@/locales/zh-CN/admin.json";
import zhAdvisor from "@/locales/zh-CN/advisor.json";
import zhAdvisorConversations from "@/locales/zh-CN/advisor-conversations.json";
import zhBilling from "@/locales/zh-CN/billing.json";
import zhCoaching from "@/locales/zh-CN/coaching.json";
import zhCommon from "@/locales/zh-CN/common.json";
import zhCustomerService from "@/locales/zh-CN/customer-service.json";
import zhKb from "@/locales/zh-CN/kb.json";
import zhLlmConfig from "@/locales/zh-CN/llm-config.json";
import zhPlatform from "@/locales/zh-CN/platform.json";
import zhPlatformPartners from "@/locales/zh-CN/platform-partners.json";
import zhPartnerPortal from "@/locales/zh-CN/partner-portal.json";
import zhPlatformPrivate from "@/locales/zh-CN/platform-private.json";
import zhPoster from "@/locales/zh-CN/poster.json";
import zhSettings from "@/locales/zh-CN/settings.json";
import zhUser from "@/locales/zh-CN/user.json";
import zhWecom from "@/locales/zh-CN/wecom.json";

export const resources = {
  "zh-CN": {
    common: zhCommon,
    advisor: zhAdvisor,
    "advisor-conversations": zhAdvisorConversations,
    poster: zhPoster,
    kb: zhKb,
    billing: zhBilling,
    admin: zhAdmin,
    "customer-service": zhCustomerService,
    coaching: zhCoaching,
    platform: zhPlatform,
    "platform-partners": zhPlatformPartners,
    "partner-portal": zhPartnerPortal,
    "platform-private": zhPlatformPrivate,
    user: zhUser,
    wecom: zhWecom,
    "llm-config": zhLlmConfig,
    settings: zhSettings,
  },
  // en: customer-service / coaching / platform / user 已提供翻译,其余仍为壳子。
  en: {
    common: enCommon,
    advisor: enAdvisor,
    "advisor-conversations": enAdvisorConversations,
    poster: enPoster,
    kb: enKb,
    billing: enBilling,
    admin: enAdmin,
    "customer-service": enCustomerService,
    coaching: enCoaching,
    platform: enPlatform,
    "platform-partners": enPlatformPartners,
    "partner-portal": enPartnerPortal,
    "platform-private": enPlatformPrivate,
    user: enUser,
    wecom: enWecom,
    "llm-config": enLlmConfig,
    settings: enSettings,
  },
} as const;

i18n.use(initReactI18next).init({
  resources,
  lng: "zh-CN",
  fallbackLng: "zh-CN",
  defaultNS: "common",
  ns: [
    "common",
    "advisor",
    "advisor-conversations",
    "poster",
    "kb",
    "billing",
    "admin",
    "customer-service",
    "coaching",
    "platform",
    "platform-partners",
    "partner-portal",
    "platform-private",
    "user",
    "wecom",
    "llm-config",
    "settings",
  ],
  interpolation: {
    escapeValue: false, // React 已转义
    // OEM 贴牌(W1-con-brand):品牌字样一律走变量 {{brandName}} / {{productName}} / {{shellName}} /
    // {{personaTitle}} …,取值 = 当前品牌(shared/brand)。直营 = DEFAULT_BRAND,渲染结果与改造前逐字相同。
    defaultVariables: brandI18nVars(getBrand(), "zh-CN"),
  },
  // 品牌 SWR 刷新后发 brandChanged,让已挂载的 useTranslation 组件按新变量重渲。
  react: { bindI18n: "languageChanged brandChanged" },
  returnNull: false,
});

/** 品牌或语言变了 → 重算 defaultVariables(i18next 每次插值都现读 options,改这里即生效)。 */
function syncBrandVariables() {
  const vars = brandI18nVars(getBrand(), i18n.language);
  // 就地改:Translator / Interpolator 持的是同一个 options 对象引用,换对象会有一处读旧值。
  if (i18n.options.interpolation) i18n.options.interpolation.defaultVariables = vars;
}
i18n.on("languageChanged", syncBrandVariables);
subscribeBrand(() => {
  syncBrandVariables();
  i18n.emit("brandChanged");
});

export default i18n;
